import Hero from 'components/hero';
import Layout from 'components/layout';
import ModuleRender from 'components/module-render';
import SEO from 'components/seo';
import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import ErrorPage from 'next/error';
import { useRouter } from 'next/router';
import { REVALIDATE_TIME } from 'settings/config';
import { __DEV__ } from 'utils/helpers';
import {
  getSpaceInfoPaths,
  SharedData,
  sharedDataQuery,
  spaceInfoQuery,
} from 'utils/queries';
import { getHeroProps, usePreviewSubscription } from 'utils/sanity';
import { getClient } from 'utils/sanity-server';

interface StudioSpaceProps {
  pageData?: Sanity.Schema.SpaceInfo;
  sharedData?: SharedData;
  preview?: boolean;
}

const StudioSpace: NextPage<StudioSpaceProps> = ({
  pageData,
  sharedData,
  preview,
}) => {
  const router = useRouter();

  const { data: page } = usePreviewSubscription(spaceInfoQuery, {
    params: { slug: pageData?.slug?.current },
    initialData: pageData,
    enabled: preview,
  });

  const { data: shared } = usePreviewSubscription(sharedDataQuery, {
    initialData: sharedData,
    enabled: preview,
  });

  if (router.isFallback) {
    return <Layout>Loading...</Layout>;
  }

  if (!page) {
    return <ErrorPage statusCode={404} />;
  }

  return (
    <Layout spaceLinks={shared.spaceInfoPaths} preview={preview}>
      <SEO
        title={page.seo?.title || page.hero?.title}
        description={page.seo?.description}
        image={page.seo?.ogImage}
      />
      <Hero {...getHeroProps(page.hero)} />
      {page.modules && (
        <ModuleRender modules={page.modules} sharedData={shared} />
      )}
    </Layout>
  );
};

export default StudioSpace;

export const getStaticProps: GetStaticProps<StudioSpaceProps> = async ({
  params,
  preview = __DEV__,
}) => {
  const pageData = await getClient(preview).fetch<Sanity.Schema.SpaceInfo>(
    spaceInfoQuery,
    {
      slug: params?.slug,
    }
  );
  const sharedData = await getClient(preview).fetch<SharedData>(
    sharedDataQuery
  );

  return {
    props: {
      preview,
      pageData,
      sharedData,
    },
    revalidate: REVALIDATE_TIME,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = await getSpaceInfoPaths(false);

  return {
    paths: paths.map(({ slug }) => ({ params: { slug } })),
    fallback: true,
  };
};
